import { FunctionComponent, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import {
	faCheck,
	faEnvelope,
	faFlask,
	faRotateRight,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { TEST_STEPS, useTestMessage, type TestStep } from "./hooks/useTestMessage";
import { colorizeMessage } from "./utils/colorizeMessage";

interface TestMessageModalProps {
	isOpen: boolean;
	message?: string;
	premiumMessage?: string;
	onClose: () => void;
}

const stripHtml = (html: string): string => {
	if (typeof window === "undefined") {
		return html;
	}

	const div = window.document.createElement("div");

	div.innerHTML = html;

	return div.textContent || div.innerText || "";
};

const buildInitials = (name?: string | null): string => {
	if (!name) {
		return "?";
	}

	const parts = name.trim().split(/\s+/);

	return parts
		.slice(0, 2)
		.map((p) => p.charAt(0).toUpperCase())
		.join("");
};

export const TestMessageModal: FunctionComponent<TestMessageModalProps> = ({
	isOpen,
	message,
	premiumMessage,
	onClose,
}) => {
	const { t } = useTranslation();
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);

	const { stepStates, result, isRunning, error, run } = useTestMessage();

	const lead = inbox[currentLeadIndex];
	const prospect = lead?.prospect;

	const leadName = useMemo(() => {
		if (!prospect) {
			return "";
		}

		const explicit = prospect.name;
		const first = prospect.firstName ?? "";
		const last = prospect.lastName ?? "";

		return explicit || `${first} ${last}`.trim();
	}, [prospect]);

	const leadMeta = useMemo(() => {
		const bits: string[] = [];

		if (prospect?.title) {
			bits.push(prospect.title);
		}

		if (prospect?.organization?.name) {
			bits.push(prospect.organization.name);
		}

		return bits.join(" · ");
	}, [prospect]);

	const runOnce = () => {
		if (!lead?.id) {
			return;
		}

		run({ listPersonId: lead.id, message: message ?? "", premiumMessage });
	};

	useEffect(() => {
		if (isOpen) {
			runOnce();
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	const renderStep = (step: TestStep, label: string, index: number) => {
		const state = stepStates[step];
		const isActive = state === "active";
		const isDone = state === "done";

		return (
			<div
				key={step}
				className={classNames(
					"flex items-center gap-3 py-2 px-1 text-[13px] transition-opacity",
					isActive && "text-text opacity-100",
					isDone && "text-text-meta opacity-100",
					!isActive && !isDone && "text-text-disabled opacity-55"
				)}
			>
				<span
					className={classNames(
						"w-[22px] h-[22px] rounded-full flex-shrink-0 flex items-center justify-center border-2 bg-white",
						isDone
							? "border-status-success bg-status-success text-white"
							: isActive
								? "border-blue-action text-blue-action"
								: "border-gray-300 text-text-disabled"
					)}
				>
					{isDone ? (
						<Icon className="w-3 h-3" icon={faCheck} />
					) : isActive ? (
						<span className="w-3 h-3 rounded-full border-2 border-blue-action-200 border-t-blue-action animate-spin" />
					) : (
						<span className="text-xs">{index + 1}</span>
					)}
				</span>
				<span>{label}</span>
			</div>
		);
	};

	const renderResult = () => {
		if (!result) {
			return null;
		}

		return (
			<div className="animate-fade-in">
				<div className="text-[11px] font-semibold uppercase tracking-wider text-text-meta mb-2 flex items-center gap-1.5">
					<Icon className="w-3 h-3" icon={faEnvelope} />
					{t("aiSnippet.testModal.generatedLabel")}
				</div>
				{result.messages.map((msg, i) => (
					<div
						key={i}
						className="border border-border-light rounded-lg p-4 bg-white text-[13px] leading-relaxed text-text mb-3"
						dangerouslySetInnerHTML={{
							__html: colorizeMessage(msg.original, lead, msg.columnValues),
						}}
					/>
				))}
				<div className="mt-3 flex items-start gap-2 text-xs text-text-meta bg-yellow/40 border border-yellow rounded-lg p-2.5">
					<span className="w-3 h-3 rounded-sm bg-yellow border-2 border-yellow-500 flex-shrink-0 mt-0.5" />
					<div>{t("aiSnippet.testModal.legend")}</div>
				</div>
			</div>
		);
	};

	const overlay = (
		<div
			className="fixed inset-0 z-[2200] flex items-center justify-center bg-[rgba(17,24,39,0.45)] backdrop-blur-[2px]"
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					onClose();
				}
			}}
		>
			<div className="w-[min(640px,94vw)] max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
				<div className="px-5 py-4 border-b border-border-light flex items-center justify-between flex-shrink-0">
					<div className="text-[15px] font-semibold inline-flex items-center gap-2.5 text-text">
						<span className="w-[26px] h-[26px] rounded-lg bg-blue-action-200 text-blue-action flex items-center justify-center">
							<Icon className="w-3.5 h-3.5" icon={faFlask} />
						</span>
						{t("aiSnippet.testModal.title")}
					</div>
					<button
						aria-label={t("aiSnippet.testModal.close")}
						className="w-8 h-8 border-0 bg-transparent rounded-md cursor-pointer text-text-meta hover:bg-background-light hover:text-text flex items-center justify-center"
						type="button"
						onClick={onClose}
					>
						<Icon className="w-4 h-4" icon={faXmark} />
					</button>
				</div>

				<div className="p-5 overflow-y-auto flex-1">
					{leadName && (
						<div className="flex items-center gap-2.5 px-3 py-2.5 bg-blue-action-200 border border-blue-action/20 rounded-lg mb-4 text-xs">
							<span className="w-[30px] h-[30px] rounded-full bg-blue-action text-white font-semibold text-[11px] flex items-center justify-center flex-shrink-0">
								{buildInitials(leadName)}
							</span>
							<div>
								<span className="font-semibold text-text">{leadName}</span>
								{leadMeta && <span className="text-text-meta"> · {leadMeta}</span>}
							</div>
						</div>
					)}

					{!result && !error && (
						<div className="flex flex-col gap-0.5">
							{TEST_STEPS.map((step, i) =>
								renderStep(step, t(`aiSnippet.testModal.steps.${step}` as const), i)
							)}
						</div>
					)}

					{error && !result && (
						<div className="text-sm text-status-error bg-status-error-light rounded-lg p-3 text-center">
							{t("aiSnippet.testModal.error")}
						</div>
					)}

					{result && renderResult()}
				</div>

				<div className="px-5 py-3.5 border-t border-border-light flex items-center justify-between flex-shrink-0">
					<span className="text-[11px] text-text-disabled">
						{isRunning
							? t("aiSnippet.testModal.generatingHint")
							: t("aiSnippet.testModal.resultHint")}
					</span>
					<div className="flex gap-2">
						<button
							className="px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer border border-gray-300 bg-white text-text-meta inline-flex items-center gap-1.5 hover:border-blue-action hover:text-blue-action disabled:opacity-50 disabled:cursor-not-allowed"
							disabled={isRunning}
							type="button"
							onClick={runOnce}
						>
							<Icon className="w-3 h-3" icon={faRotateRight} />
							{t("aiSnippet.testModal.regenerate")}
						</button>
						<button
							className="px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer bg-blue-action border border-blue-action text-white inline-flex items-center gap-1.5 hover:bg-blue-action-dark disabled:opacity-50 disabled:cursor-not-allowed"
							disabled={!result || isRunning}
							type="button"
							onClick={onClose}
						>
							<Icon className="w-3 h-3" icon={faCheck} />
							{t("aiSnippet.testModal.done")}
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	return ReactDOM.createPortal(overlay, document.body);
};

// Silence unused-import warning for stripHtml (kept for future variable/snippet
// extraction enhancements).
void stripHtml;
